// ถิ่นที่อยู่ของชนิดเป็นช่องกริด (docs/PLAN.md ข้อ 4.1–4.2)
// มีช่องจากบันทึกการพบ (crawler) ก็ใช้ช่องนั้น ถ้ามีแค่รายชื่อประเทศ ใช้ช่องของประเทศนั้นที่ประเภทพื้นที่ตรงกับชนิด

import { cellCenter, decodeCells, neighbors, type RangeCell } from "../engine/grid";
import type { Traits } from "../engine/types";
import type { CountryAreas } from "../shared/api";
import { base64ToBytes } from "../shared/base64";
import { fittingBiomes } from "./biomes";
import { FULL_LAND, countryCells, hasLand, hasSea, landBiomeAt, type GeoGrid } from "./geodata";

/** สัตว์อยู่บนบก (รวมน้ำจืด) ในทะเล หรือได้ทั้งสองแบบ */
export type Realm = "land" | "sea" | "both";

export function realmOf(traits: Pick<Traits, "habitat">): Realm {
  const sea = traits.habitat.includes("sea");
  if (!sea) return "land";
  return traits.habitat.length === 1 ? "sea" : "both";
}

export interface SpeciesRange {
  cells: RangeCell[];
  source: "records" | "countries"; // records = จากบันทึกการพบจริง, countries = ประมาณจากรายชื่อประเทศ
}

const COAST_REACH = 2; // ทะเลของประเทศ = ช่องที่มีน้ำในระยะกี่ช่องจากแผ่นดินของประเทศนั้น

/**
 * ระดับสีบนแผนที่ของถิ่นที่อยู่โดยประมาณ: ช่องที่เป็นพื้นที่แบบนั้นทั้งช่องเข้มกว่าช่องที่ปนกัน
 * (ไม่ใช่ความหนาแน่นของการพบจริง แต่ใช้ถ่วงจุดเกิดให้ไม่ไปตกบนแหลมเล็กๆ)
 */
function fallbackLevel(grid: GeoGrid, cell: number, realm: Realm): 1 | 2 {
  if (realm === "land") return grid.land[cell] === FULL_LAND ? 2 : 1;
  if (realm === "sea") return grid.land[cell] === 0 ? 2 : 1;
  return hasLand(grid, cell) && hasSea(grid, cell) ? 2 : 1;
}

function inAreas(cell: number, boxes: CountryAreas[string] | undefined): boolean {
  if (!boxes) return true;
  const { lat, lon } = cellCenter(cell);
  return boxes.some(([s, w, n, e]) => lat >= s && lat <= n && lon >= w && lon <= e);
}

const HIGHLAND = 1500; // เมตร สัตว์ภูเขาอยู่ได้ทุก biome ที่สูงเกินนี้

/** ช่องบนบกที่ประเภทพื้นที่เหมาะกับชนิดนี้ ถ้าไม่มีช่องไหนเหมาะเลย (ข้อมูลหยาบ) ใช้ทุกช่อง */
function fittingLand(grid: GeoGrid, cells: number[], traits: Pick<Traits, "habitat">): number[] {
  const fits = fittingBiomes(traits.habitat);
  if (!fits) return cells;
  const mountain = traits.habitat.includes("mountain");
  const kept = cells.filter((c) => {
    const b = landBiomeAt(grid, c);
    return (b !== null && fits.has(b)) || (mountain && grid.elevation[c] >= HIGHLAND);
  });
  return kept.length ? kept : cells;
}

export function rangeFromCountries(
  codes: readonly string[],
  grid: GeoGrid,
  traits: Pick<Traits, "habitat">,
  areas: CountryAreas = {},
): RangeCell[] {
  const realm = realmOf(traits);
  const out = new Set<number>();
  for (const code of codes) {
    const land = countryCells(grid, code).filter((c) => inAreas(c, areas[code]));
    if (realm !== "sea") for (const c of fittingLand(grid, land.filter((c) => hasLand(grid, c)), traits)) out.add(c);
    if (realm !== "land") {
      for (const c of land) {
        if (hasSea(grid, c)) out.add(c);
        for (const n of neighbors(c, realm === "sea" ? COAST_REACH : 1)) if (hasSea(grid, n)) out.add(n);
      }
    }
  }
  return [...out].sort((a, b) => a - b).map((cell) => ({ cell, level: fallbackLevel(grid, cell, realm) }));
}

export interface RangeInput {
  rangeCells?: string | null; // BLOB จาก species_range.cells ในรูป base64
  countries: readonly string[];
  areas?: CountryAreas;
}

export function resolveRange(input: RangeInput, grid: GeoGrid, traits: Pick<Traits, "habitat">): SpeciesRange | null {
  if (input.rangeCells) {
    try {
      const cells = decodeCells(base64ToBytes(input.rangeCells));
      if (cells.length) return { cells, source: "records" };
    } catch {
      // ข้อมูลเสียก็ลองใช้รายชื่อประเทศแทน
    }
  }
  const cells = rangeFromCountries(input.countries, grid, traits, input.areas);
  return cells.length ? { cells, source: "countries" } : null;
}
